import { useState, useRef, useMemo, useEffect, MouseEventHandler } from "react";
// Materials
import { GridCellProps, GridColDef, useGridApiRef } from "@mui/x-data-grid-premium";
// Custom components
import AddCellIssues from "@/Layouts/components/Tables/DataSmartTable/additional/AddCellIssues";
import CommonToolbar from "@/Layouts/components/Tables/DataSmartTable/toolbar/CommonToolbar";
import NoRowsOverlay from "@/Layouts/components/Tables/DataSmartTable/additional/NoRowsOverlay";
import DataSmartTable from "@/Layouts/components/Tables/DataSmartTable";
import { Nullable } from "@/Common/interfaces/types";
import { errorLogger, getError } from "@/Common/helpers/logger";
import { CommonSalesTableProps } from "@/Washouts/types/commons";

// Types
import { Issue } from "@/Common/types/tables";

export interface InformationTableProps extends CommonSalesTableProps {
	colDefFn: (reference: string, issues: Issue[]) => GridColDef[];
	negativeColumns?: string[];
}

export function InformationTable(props: Readonly<InformationTableProps>) {
	const apiRef = useGridApiRef();
	const [totalColumns, setTotalColumns] = useState<GridColDef[]>([]);
	// Issues support attributes
	const [openAddIssues, setOpenAddIssues] = useState(false);
	const [anchorAddIssues, setAnchorAddIssues] = useState<(HTMLDivElement & EventTarget) | null>(null);
	const [issues, setIssues] = useState<Issue[]>([]);
	const activeIssue = useRef<Issue>(null);

	const title = props.title ?? "Information";

	const handleClickCell: MouseEventHandler<HTMLDivElement> = (event) => {
		activeIssue.current = {
			rowReference: event.currentTarget?.parentElement?.dataset.id, // _id
			columnReference: event.currentTarget?.dataset?.field, // field
		};
		setAnchorAddIssues(event.currentTarget);
		setOpenAddIssues(true);
	};

	const handleChangeIssues = (color: Nullable<string>) => {
		let currentIssues = [...issues];
		if (color) {
			let isPresent = false;
			for (const issue of currentIssues) {
				if (
					issue &&
					issue.rowReference === activeIssue?.current?.rowReference &&
					issue.columnReference === activeIssue?.current?.columnReference
				) {
					issue.color = color;
					isPresent = true;
					break;
				}
			}

			if (!isPresent) {
				currentIssues.push({
					color,
					tableReference: props?.issuesReference,
					rowReference: activeIssue?.current?.rowReference,
					columnReference: activeIssue?.current?.columnReference,
				});
			}
		} else {
			currentIssues?.forEach((issue, key) => {
				if (
					issue &&
					issue.rowReference === activeIssue?.current?.rowReference &&
					issue.columnReference === activeIssue?.current?.columnReference
				) {
					currentIssues[key] = null;
				}
			});
			currentIssues = currentIssues.filter((issue) => issue);
		}

		setIssues(currentIssues);
		props.onIssuesUpdatedAction?.(currentIssues, props?.issuesReference);
	};

	useMemo(() => {
		setTotalColumns(props.colDefFn(props.issuesReference ?? "", issues));
	}, [props, issues]);

	useEffect(() => {
		// Clear filter on purpose
		try {
			const filter = apiRef.current?.state?.filter;
			if (filter) {
				const cleanFilter = {
					filterModel: { items: [], linkOperator: "and" },
					visibleRows: null,
					visibleRowsLookup: {},
				};

				apiRef.current.setState((prevState) => ({
					...prevState,
					filter: { ...filter, ...cleanFilter },
				}));
			}
		} catch (error) {
			errorLogger(getError(error));
		}

		setIssues(props.issues ?? []);

		return () => {
			// Issues controls
			setIssues([]);
			activeIssue.current = null;
			setOpenAddIssues(false);
			setAnchorAddIssues(null);
		};
	}, [props, apiRef]);

	if (!totalColumns) {
		return null;
	}

	function getCell(): Partial<GridCellProps> | undefined {
		if (props?.issues) {
			return { onClick: handleClickCell };
		}
	}

	return (
		<>
			<DataSmartTable
				{...props.tableProps}
				sx={props.sxCustom}
				apiRef={apiRef}
				columns={totalColumns}
				rows={props.data || []}
				hideFooter
				disableColumnMenu
				disableRowSelectionOnClick
				disableMultipleRowSelection
				checkboxSelection={false}
				columnVisibilityModel={{
					_id: false,
				}}
				initialState={{
					aggregation: {
						model: {
							amount: "sum",
						},
					},
				}}
				slots={{
					toolbar: CommonToolbar,
					noRowsOverlay: NoRowsOverlay,
				}}
				slotProps={{
					toolbar: {
						customTitle: {
							hide: false,
							label: title,
							sx: { mb: "15px" },
						},
					},
					noRowsOverlay: {
						title: `No ${title} Data`,
					},
					cell: getCell(),
				}}
				getCellClassName={(params) => {
					// Targeting footer
					if (params.id === "auto-generated-group-footer-root") {
						if (props.negativeColumns?.includes(params.field) && typeof params.value === "number" && params.value < 0) {
							return "aggregation-footer-negative";
						}

						// Append label 'Total' to the cell value in footer
						if (params.field === "date") {
							return "aggregation-footer-total";
						}
					}
					return "";
				}}
				customProps={{}}
			/>
			{props.addIssues && (
				<AddCellIssues
					open={openAddIssues}
					anchorEl={anchorAddIssues}
					onCloseAction={() => {
						setOpenAddIssues(false);
						setAnchorAddIssues(null);
					}}
					onChangeAction={handleChangeIssues}
				/>
			)}
		</>
	);
}
